import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/apiClient";

// --- Rate of growth types (GET /api/growth/<clientId>) ---

export type GrowthSource = "social" | "businessProfile" | "website";

export type GrowthPillarKey = "audience" | "visibility" | "engagement" | "actions";

export interface GrowthMetric {
  key: string;
  label: string;
  source: GrowthSource;
  current: number | null;
  previous: number | null;
  /** Clamped to [-100, +100]; null when either month is missing or the
   *  previous month is 0. */
  changePct: number | null;
  /** On a provisional month: a metric that exists only as a monthly total
   *  (social impressions, engagement, Instagram clicks), so its change waits
   *  for month end. */
  atMonthEnd: boolean;
  /** ISO days the metric's windows end on a provisional month, this month's
   *  and the month before's; null once complete or for an atMonthEnd
   *  metric. */
  through: string | null;
  previousThrough: string | null;
  /** On a provisional month: the metric's source has no settled day in the
   *  month yet, so its values show with no change. */
  noSettledDays: boolean;
}

export interface GrowthPillar {
  key: GrowthPillarKey;
  label: string;
  /** Mean of the pillar's non-null metric changes. */
  changePct: number | null;
  metrics: GrowthMetric[];
}

/** Month M against M-1, organic only (paid never feeds it, DD-021). */
export interface Growth {
  year: number;
  month: number;
  /** Mean of the non-null pillars; null when no pillar has a value. */
  rate: number | null;
  /** Every contributing source's month is complete. */
  complete: boolean;
  /** Not complete, so the rate and changes compare each metric's same-days
   *  windows, and atMonthEnd metrics carry no change. */
  provisional: boolean;
  /** ISO date the month becomes final; null once complete. */
  finalFrom: string | null;
  sources: GrowthSource[];
  /** Sources whose sync is behind; the rate leaves them out. */
  overdue: GrowthSource[];
  pillars: GrowthPillar[];
}

export const growthKeys = {
  all: (clientId: string) => ["growth", clientId] as const,
  month: (clientId: string, year: number, month: number) =>
    [...growthKeys.all(clientId), year, month] as const,
};

export const useGrowth = (
  clientId: string,
  year: number,
  month: number
) => {
  // Cached per client and month, so the Reporting rail and a tile showing
  // the same panel share one request.
  const query = useQuery({
    queryKey: growthKeys.month(clientId, year, month),
    queryFn: () => apiClient.get<Growth>(`/api/growth/${clientId}`, { params: { year, month } }),
    enabled: Boolean(clientId),
  });
  const apiErr = query.error as { response?: { data?: { error?: string } }; message?: string } | null;
  const error = apiErr ? apiErr.response?.data?.error || apiErr.message || "Failed to load growth" : null;

  return { data: query.data ?? null, isLoading: query.isLoading, error };
};
